import React, { Fragment, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '..';
import RoomInputCounter from './RoomInputCounter';

import { formatStringsToPlural } from '../../utils';
import type { RoomInputCounterConstructor, RoomInputCounterData, RoomInputData, RoomOpenState } from './types';

interface IRoomInputProps {
  data: RoomInputData;
  isFirstRoom: boolean;
  isOpen: boolean;
  isOpenDisabled: boolean;
  onToggle: (roomState: RoomOpenState) => void;
  onChange: (newData: RoomInputData) => void;
  onDelete: (data: RoomInputData) => void;
}

const RoomInput = ({
  data,
  isFirstRoom,
  isOpen,
  isOpenDisabled,
  onToggle,
  onChange,
  onDelete,
  ...props
}: IRoomInputProps) => {
  const { t } = useTranslation();

  const ROOM_INPUT_COUNTER_DATA: RoomInputCounterConstructor[] = [
    { category: 'adults', min: 1, max: 4 },
    { category: 'children', min: 0, max: 3 },
  ];

  useEffect(() => {
    const component = document.querySelector(`#room-${data.id}`) as HTMLDivElement;
    const headerArrow = component.querySelector('.room-input-header-arrow') as HTMLElement;

    if (isOpen) {
      component.classList.add('open');
      headerArrow.classList.add('open');
    } else {
      component.classList.remove('open');
      headerArrow.classList.remove('open');
    }
  }, [data, isOpenDisabled, isOpen]);

  const handleExpand = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    onToggle({ id: data.id, isOpen: !isOpen });
  };

  const handleDelete = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onDelete(data);
  };

  const handleCounterChange = (counterData: RoomInputCounterData) => {
    onChange({ ...data, [counterData.category]: counterData.data });
  };

  return (
    <section className="room-input-container" id={`room-${data.id}`} {...props}>
      <button className="room-input-header" type="button" disabled={isOpenDisabled} onClick={handleExpand}>
        <article className="room-input-header-text-wrapper">
          <span className="room-input-header-title">{t('counterInput.roomInput.roomTitle', { id: data.id })}</span>
          <span className="room-input-header-total-amount">
            {data.children.length > 0
              ? t('counterInput.roomInput.totalAmount', {
                  adults: formatStringsToPlural('adulto', data.adults),
                  children: formatStringsToPlural('criança', data.children.length),
                })
              : t('counterInput.roomInput.totalAmountAdultsOnly', {
                  adults: formatStringsToPlural('adulto', data.adults),
                })}
          </span>
        </article>

        <section className="room-input-header-icons-wrapper">
          {!isFirstRoom && (
            <button className="room-input-header-delete-button" type="button" onClick={handleDelete}>
              <Icon className="room-input-header-delete-icon" icon="delete" round />
            </button>
          )}

          <Icon className="room-input-header-arrow" icon="arrow_drop_down" round />
        </section>
      </button>

      <section className="room-input-content">
        {ROOM_INPUT_COUNTER_DATA.map(({ category, max, min }, index) => (
          <Fragment key={`room-input-${data.id}-${category}-counter`}>
            <RoomInputCounter
              id={data.id}
              data={data}
              category={category}
              max={max}
              min={min}
              onCounterChange={handleCounterChange}
            />

            {index === 0 && <div className="room-input-counter-divider" />}
          </Fragment>
        ))}
      </section>
    </section>
  );
};

export default RoomInput;
